var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState, useMemo, } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { autoUpdate, flip, FloatingFocusManager, FloatingPortal, hide, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole, } from '@floating-ui/react';
import Input from '@storeblocks/input';
import Calendar from '@storeblocks/calendar';
import { isValidRange } from './utils';
function RangeDatePicker(_a) {
    var { id, name, label, value, onChange, onBlur, format = 'dd.MM.yyyy', rangeSeparator = '-', locale = 'no', min, max, hint = `Format: ${format} ${rangeSeparator} ${format}`, clearable = false, clearLabel = locale === 'no' ? 'Nullstill' : 'Clear', disabled, disabledDate, error, warning, success, small, medium, large = true, fluid, labelProps, labelPopover, hintProps, inputProps = {}, attentionProps } = _a, props = __rest(_a, ["id", "name", "label", "value", "onChange", "onBlur", "format", "rangeSeparator", "locale", "min", "max", "hint", "clearable", "clearLabel", "disabled", "disabledDate", "error", "warning", "success", "small", "medium", "large", "fluid", "labelProps", "labelPopover", "hintProps", "inputProps", "attentionProps"]);
    const getDateRangeFromString = useCallback((incoming) => {
        const incomingRange = incoming
            .split(rangeSeparator)
            .map((item) => item.trim());
        const incomingLuxonRange = incomingRange.map((item) => DateTime.fromFormat(item, format));
        return incomingLuxonRange.map((item) => item.toJSDate());
    }, [rangeSeparator, format]);
    const getRangeStringFromDateTimes = useCallback(([start, end]) => `${start.toFormat(format)} ${rangeSeparator} ${end.toFormat(format)}`, [rangeSeparator, format]);
    const getRangeStringFromDateRange = useCallback((range) => getRangeStringFromDateTimes(range.map((item) => DateTime.fromJSDate(item))), [getRangeStringFromDateTimes]);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(isValidRange(value) ? getRangeStringFromDateRange(value) : '');
    const { x, y, refs, strategy, context } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            shift(),
            offset(8),
            hide(),
            flip({
                fallbackPlacements: ['right', 'left', 'bottom', 'top-start'],
            }),
        ],
        placement: 'bottom-start',
        strategy: 'fixed',
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context),
        useDismiss(context),
    ]);
    const handleInputChange = useCallback((event) => {
        const { value: incoming } = event.target;
        const empty = incoming === '';
        const containsLetters = /[a-zA-Z]/.test(incoming);
        if (empty) {
            setInputValue('');
            onChange(event, undefined);
        }
        else if (!containsLetters) {
            setInputValue(incoming);
            onChange(event, getDateRangeFromString(incoming));
        }
    }, [getDateRangeFromString, onChange]);
    const handleInputBlur = useCallback((event) => {
        if (isValidRange(value)) {
            setInputValue(getRangeStringFromDateRange(value));
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    }, [value, getRangeStringFromDateRange, onBlur]);
    const handleCalendarChange = useCallback((range) => {
        const incoming = isValidRange(range)
            ? getRangeStringFromDateRange(range)
            : '';
        setInputValue(incoming);
        onChange(undefined, range);
        setOpen(false);
    }, [onChange, getRangeStringFromDateRange]);
    const maxInputLength = useMemo(() => 2 * format.length + rangeSeparator.length + 2, [format, rangeSeparator]);
    return (React.createElement(DatePickerBase, Object.assign({}, props),
        React.createElement(Input, { id: id, name: name, label: label, hint: hint, small: small, medium: medium, large: large, fluid: fluid, value: inputValue, onChange: handleInputChange, onBlur: handleInputBlur, error: error, warning: warning, success: success, ref: refs.setReference, icon: "calendar", disabled: disabled, iconButtonProps: getReferenceProps(), onIconClick: () => {
                setOpen(!open);
            }, labelProps: labelProps, labelPopover: labelPopover, hintProps: hintProps, inputProps: Object.assign({ type: 'tel', maxLength: maxInputLength }, inputProps), attentionProps: attentionProps }),
        React.createElement(FloatingPortal, null, open && (React.createElement(FloatingFocusManager, { context: context },
            React.createElement(Calendar, Object.assign({ id: `${id}-calendar`, name: `${name}-calendar`, range: isValidRange(value) ? value : [null, null], onRangeChange: handleCalendarChange, locale: locale, clearable: clearable, clearLabel: clearLabel, max: max, min: min, selectRange: true, disabledDate: disabledDate }, getFloatingProps({
                ref: refs.setFloating,
                style: {
                    zIndex: 2,
                    position: strategy,
                    top: y !== null && y !== void 0 ? y : 0,
                    left: x !== null && x !== void 0 ? x : 0,
                },
            }))))))));
}
export const DatePickerBase = styled.div.attrs({
    className: 'rangedatepicker',
}) ``;
export default RangeDatePicker;
